// Pure helpers for the tagger playground: how a run's schema source, a draft's
// state and a typed schema diff are rendered. No React, no fetch, so the shape
// of what a reader sees is testable without a page.
import type { SchemaBlockDiff, SchemaDiff, TagDraft } from "@bi/shared/types";

/** The first eight hex characters: enough to tell two schemas apart in a table
 *  cell, and what the release sha is usually quoted as anyway. */
export function shortSha(sha: string | null | undefined): string {
  return (sha ?? "").slice(0, 8);
}

/** A run's `schemaSource` as a chip label. `draft:<id>` is resolved to the
 *  draft's name when the draft still exists, and to its short id when not. */
export function schemaSourceLabel(
  source: string,
  sha: string,
  drafts: ReadonlyArray<Pick<TagDraft, "id" | "name">> = [],
): string {
  // A run row from a service older than migration 0008 carries no source; read it as the
  // vendored file, which is what every run was before the mirror existed.
  source = source || "vendored";

  if (source.startsWith("online:")) return `online ${shortSha(source.slice("online:".length))}`;
  if (source.startsWith("draft:")) {
    const id = source.slice("draft:".length);
    const draft = drafts.find((d) => d.id === id);
    return draft ? `draft: ${draft.name || shortSha(draft.id)}` : `draft ${shortSha(id)}`;
  }
  if (source === "override") return `pasted ${shortSha(sha)}`;
  return "vendored";
}

/** The badge tone for a draft's state. */
export function draftStateTone(state: string): "success" | "warning" | "neutral" {
  if (state === "promoted") return "success";
  if (state === "diverged") return "warning";
  return "neutral";
}

/** One line per thing that differs inside a block, in the order a reader
 *  decides what to commit: prose first, then tags, then the response schema. */
export function blockDiffLines(block: SchemaBlockDiff): string[] {
  const out: string[] = [];
  if (block.preambleDiff.length) out.push(`prose: ${block.preambleDiff.length} diff lines`);
  for (const id of block.tagsAdded) out.push(`tag added: ${id}`);
  for (const id of block.tagsRemoved) out.push(`tag removed: ${id}`);
  for (const c of block.tagsChanged) out.push(`tag changed: ${c.id}.${c.field}`);
  for (const p of block.propsAdded) out.push(`schema property added: ${p}`);
  for (const p of block.propsRemoved) out.push(`schema property removed: ${p}`);
  for (const p of block.propsChanged) out.push(`schema property changed: ${p.path}`);
  return out;
}

/** The one-sentence summary above a diff. */
export function diffSummary(diff: SchemaDiff): string {
  if (diff.onlineSha == null) return "Nothing online has been ingested yet, so there is nothing to compare against.";
  if (diff.identical) return "Identical to what is online.";
  const n =
    diff.blocksAdded.length +
    diff.blocksRemoved.length +
    diff.blocks.reduce((acc, b) => acc + blockDiffLines(b).length, 0);
  const noun = n === 1 ? "difference" : "differences";
  const state =
    diff.state === "diverged"
      ? " This draft was promoted once and online has moved since."
      : "";
  return `${n} ${noun} against online ${shortSha(diff.onlineSha)}.${state}`;
}

/** A value from a tag or property change, as a cell. Strings are shown as
 *  they are; anything else as compact JSON. */
export function changeValue(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  return JSON.stringify(value);
}
